import { TreatmentFileKind, TreatmentScanType } from 'app/core/enums';
import type { TreatmentTypeFile } from 'app/core/models';

/**
 * Returns the file configuration that the server will use when an attachment is saved.
 * When allowed file configurations are provided, their active original-file formats replace
 * the attachment's unrestricted accept list without changing its persisted file kind or type.
 */
export function resolveAttachmentFileConfiguration(
  files: readonly TreatmentTypeFile[],
  usesOtherFileKind: boolean,
  allowedFileConfigurations?: readonly TreatmentTypeFile[] | null
): TreatmentTypeFile | null {
  const attachmentCandidates = files.filter(file => file.fileKind === TreatmentFileKind.Other && file.isActive);
  const attachmentFile =
    attachmentCandidates.find(file => file.type === TreatmentScanType.Other) ?? attachmentCandidates[0] ?? null;
  if (attachmentFile === null) return null;

  const fileKind = usesOtherFileKind ? TreatmentFileKind.Other : TreatmentFileKind.Design;
  const resolvedAttachmentFile =
    files.find(file => file.fileKind === fileKind && file.isActive && file.type === attachmentFile.type) ?? null;
  if (resolvedAttachmentFile === null || allowedFileConfigurations == null) return resolvedAttachmentFile;

  const allowedFormats = new Map<string, string>();
  allowedFileConfigurations
    .filter(file => file.fileKind === TreatmentFileKind.Original && file.isActive)
    .flatMap(file => file.accept.split(','))
    .map(format => format.trim())
    .filter(format => format.length > 0 && !['*', '*.*', '*/*'].includes(format.toLowerCase()))
    .forEach(format => {
      const normalizedFormat = format.toLowerCase();
      if (!allowedFormats.has(normalizedFormat)) {
        allowedFormats.set(normalizedFormat, format);
      }
    });

  if (allowedFormats.size === 0) return null;

  return {
    ...resolvedAttachmentFile,
    accept: Array.from(allowedFormats.values()).join(','),
  };
}
